import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import FAQAccordion from "@/components/FAQAccordion";
import ChantierReview from "@/components/ChantierReview";
import { PageHero, SectionHead, CTABand, Reveal } from "@/components/Ui";
import { RelatedLinks } from "@/pages/ServicePage";
import { CHANTIERS } from "@/data/chantiers";

export default function ChantierPage() {
  const { slug } = useParams();
  const chantier = CHANTIERS[slug];

  if (!chantier) return <Navigate to="/realisations" replace />;

  const localisation = chantier.localisation || chantier.ville;
  const zonePhrase = chantier.zonePhrase || `à ${chantier.ville}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: chantier.titre,
    description: chantier.seoDesc,
    image: chantier.photos.map((p) => `https://dmznormandie.fr${p.img}`),
    provider: {
      "@type": "GeneralContractor",
      name: "DMZ Normandie",
      telephone: "+33745049189",
      email: "dmznormandie@gmail.com",
      url: "https://dmznormandie.fr",
      address: {
        "@type": "PostalAddress",
        streetAddress: "7 rue Jean de la Fontaine",
        postalCode: "27000",
        addressLocality: "Évreux",
        addressRegion: "Normandie",
        addressCountry: "FR",
      },
    },
    areaServed: [chantier.ville, ...chantier.villesProches],
  };

  const faq = [
    {
      q: `Intervenez-vous pour ce type de chantier ${zonePhrase} ?`,
      a: `Oui. Ce chantier a été réalisé par DMZ Normandie ${zonePhrase}. Nous pouvons étudier un projet similaire ${zonePhrase} et dans les communes proches.`,
    },
    {
      q: "Comment obtenir un devis pour un projet similaire ?",
      a: "Contactez-nous par téléphone ou via le formulaire de devis en décrivant votre projet. La visite et le devis sont gratuits.",
    },
  ];

  return (
    <>
      <SEO title={chantier.seoTitle} description={chantier.seoDesc} path={`/chantiers/${chantier.slug}`} jsonLd={jsonLd} />
      <PageHero title={chantier.titre} subtitle={chantier.subtitle} />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/realisations" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-[#C4A484] transition-colors mb-10" data-testid="retour-realisations">
            <ArrowLeft size={14} /> Retour aux réalisations
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16">
            <Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="chantier-gallery">
                {chantier.photos.map((photo, i) => (
                  <figure key={photo.img} className={`overflow-hidden relative ${i === 0 ? "sm:col-span-2" : ""}`}>
                    <img src={photo.img} alt={photo.alt} loading={i === 0 ? "eager" : "lazy"} className={`w-full object-cover ${i === 0 ? "h-72 md:h-[430px]" : "h-56"}`} />
                    <div className="absolute inset-0 bg-black/15" />
                  </figure>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="card-dark p-8 lg:p-10 lg:sticky lg:top-28" data-testid="chantier-summary">
                <p className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#C4A484] mb-5">
                  <MapPin size={14} /> Chantier réel — {localisation}
                </p>
                <p className="text-zinc-400 font-light leading-relaxed">{chantier.description}</p>
                <p className="mt-6 text-sm font-light text-zinc-500 border-l-2 border-[#C4A484] pl-5">
                  Secteurs proches : {chantier.villesProches.join(" · ")}
                </p>
                <Link to="/contact" className="btn-sable mt-8" data-testid="chantier-devis-cta">
                  Demander un devis similaire <ArrowRight size={16} />
                </Link>
                <ChantierReview service={chantier.services[0]} />
              </div>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-20">
            <Reveal>
              <SectionHead overline="Matériaux" title="Les matériaux utilisés" />
              <div className="space-y-4">
                {chantier.materiaux.map((materiau) => (
                  <p key={materiau} className="flex items-start gap-3 text-zinc-300 font-light">
                    <CheckCircle2 size={18} className="text-[#C4A484] mt-0.5 shrink-0" /> {materiau}
                  </p>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <SectionHead overline="Étapes" title="Comment le chantier a avancé" />
              <div className="space-y-5">
                {chantier.etapes.map((etape, i) => (
                  <div key={etape} className="flex gap-4 border-b border-white/10 pb-5" data-testid={`chantier-etape-${i}`}>
                    <span className="font-serif-display text-2xl text-[#C4A484]">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm font-light text-zinc-400 leading-relaxed pt-1">{etape}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="max-w-4xl mx-auto mt-20">
            <SectionHead overline="Questions" title={`Votre projet à ${chantier.ville}`} />
            <FAQAccordion items={faq} testid={`faq-chantier-${chantier.slug}`} />
          </div>

          <Reveal className="mt-20">
            <p className="text-[10px] tracking-[0.5em] uppercase text-[#C4A484] mb-5">Voir aussi</p>
            <RelatedLinks slugs={chantier.services} />
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
